import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";
import { createAnalysis } from "@/lib/api";
import { listWorkers } from "@/lib/workers";
import { UploadSimple, FilmStrip, ArrowRight, Warning, X, VideoCamera, Stack } from "@phosphor-icons/react";

const INDUSTRIES = [
  "Manufacturing", "Warehouse & Logistics", "Construction", "Healthcare",
  "Retail & Hospitality", "Agriculture", "Office / Computer Work", "Other"
];

const TASK_TYPES = [
  "Lifting / Lowering", "Pushing / Pulling", "Carrying", "Reaching Overhead",
  "Bending / Stooping", "Twisting", "Repetitive Hand Motion", "Static Posture",
  "Patient Handling", "Tool Operation", "Other"
];

export default function UploadAnalysis() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [title, setTitle] = useState("");
  const [industry, setIndustry] = useState(INDUSTRIES[0]);
  const [taskType, setTaskType] = useState(TASK_TYPES[0]);
  const [duration, setDuration] = useState(120);
  const [notes, setNotes] = useState("");
  const [workerId, setWorkerId] = useState(searchParams.get("worker") || "");
  const [workers, setWorkers] = useState([]);
  const [blurFaces, setBlurFaces] = useState(true);
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    (async () => { try { setWorkers(await listWorkers()); } catch (e) {} })();
  }, []);

  const onSelect = (f) => {
    if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("Please select a video file"); return; }
    if (f.size > 120 * 1024 * 1024) { toast.error("Max file size is 120MB"); return; }
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    onSelect(e.dataTransfer.files?.[0]);
  };

  const clearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null); setPreviewUrl(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!file) { toast.error("Pick a video first"); return; }
    if (!title.trim()) { toast.error("Give your analysis a title"); return; }

    const form = new FormData();
    form.append("file", file);
    form.append("title", title);
    form.append("industry", industry);
    form.append("task_type", taskType);
    form.append("duration_minutes", String(duration));
    if (workerId) form.append("worker_id", workerId);
    if (notes) form.append("notes", notes);
    form.append("blur_faces", String(blurFaces));

    setSubmitting(true);
    setProgress(0);
    try {
      const created = await createAnalysis(form, setProgress);
      toast.success("Upload complete — analysis started");
      nav(`/dashboard/analysis/${created.id}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Upload failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar variant="app" />
      <Toaster richColors position="top-right" />

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-2">New analysis</div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-ink-900">Upload a task video.</h1>
          <Link to="/dashboard/upload-bulk">
            <Button variant="outline" size="sm" data-testid="upload-bulk-link" className="rounded-none border-ink-100 hover:border-ink-900">
              <Stack size={14} weight="bold" className="mr-1.5" /> Bulk upload
            </Button>
          </Link>
        </div>
        <p className="text-ink-700 max-w-2xl">15–90 seconds of the repetitive motion, side angle preferred. SafeForm.ai will return a per-region hazard report and a written safer technique.</p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Drop zone */}
          <div className="lg:col-span-2">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`border ${dragOver ? "border-safety bg-safety/5" : "border-ink-100"} transition-colors`}
              data-testid="upload-dropzone"
            >
              <input
                ref={fileRef}
                type="file"
                accept="video/*"
                className="hidden"
                data-testid="upload-file-input"
                onChange={(e) => onSelect(e.target.files?.[0])}
              />
              <input
                ref={cameraRef}
                type="file"
                accept="video/*"
                capture="environment"
                className="hidden"
                data-testid="upload-camera-input"
                onChange={(e) => onSelect(e.target.files?.[0])}
              />
              {!file ? (
                <div className="p-12 sm:p-16 text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-ink-900 mb-4">
                    <UploadSimple size={22} weight="bold" className="text-white" />
                  </div>
                  <div className="font-display font-bold text-2xl tracking-tight text-ink-900">Drop a video here</div>
                  <div className="mt-2 text-ink-500">or pick how to add the clip · mp4, mov, webm · up to 120 MB</div>
                  <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
                    <Button
                      type="button"
                      data-testid="upload-browse"
                      onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                      size="lg"
                      className="rounded-none bg-ink-900 hover:bg-safety text-white h-12 px-6"
                    >
                      <UploadSimple size={16} weight="bold" className="mr-2" /> Choose file
                    </Button>
                    <Button
                      type="button"
                      data-testid="upload-record"
                      onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
                      size="lg"
                      variant="outline"
                      className="rounded-none border-ink-900 hover:bg-ink-900 hover:text-white h-12 px-6"
                    >
                      <VideoCamera size={16} weight="bold" className="mr-2" /> Record on phone
                    </Button>
                  </div>
                  <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500">
                    Tip · install SafeForm as a phone app to record right from the job site
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <video src={previewUrl} controls className="w-full max-h-[480px] bg-black" />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); clearFile(); }}
                    className="absolute top-3 right-3 px-2 py-1 bg-white border border-ink-900 text-ink-900 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-ink-900 hover:text-white"
                    data-testid="upload-clear-file"
                  >
                    <X size={12} weight="bold" className="inline mr-1" /> Replace
                  </button>
                  <div className="px-4 py-3 bg-ink-50 border-t border-ink-100 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <FilmStrip size={14} weight="bold" />
                      <span className="truncate">{file.name}</span>
                    </div>
                    <div className="font-mono text-xs text-ink-500 shrink-0">{(file.size / (1024 * 1024)).toFixed(1)} MB</div>
                  </div>
                </div>
              )}
            </div>

            {submitting && (
              <div className="mt-4 border border-ink-100 p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-2">Uploading…</div>
                <div className="h-1 bg-ink-100">
                  <div className="h-full bg-safety transition-all" style={{ width: `${progress}%` }} />
                </div>
                <div className="mt-2 font-mono text-xs text-ink-700">{progress}%</div>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="lg:col-span-1 border border-ink-100 p-6">
            <div className="space-y-5">
              <div>
                <Label htmlFor="title" className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Analysis title</Label>
                <Input id="title" data-testid="upload-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Picker bay 3 — left side bin lift" className="mt-2 rounded-none border-ink-100 focus-visible:ring-safety" />
              </div>

              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Industry</Label>
                <Select value={industry} onValueChange={setIndustry}>
                  <SelectTrigger data-testid="upload-industry" className="mt-2 rounded-none border-ink-100"><SelectValue /></SelectTrigger>
                  <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Task type</Label>
                <Select value={taskType} onValueChange={setTaskType}>
                  <SelectTrigger data-testid="upload-task-type" className="mt-2 rounded-none border-ink-100"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="duration" className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Per-shift duration (min)</Label>
                <Input id="duration" data-testid="upload-duration" type="number" min={1} max={720} value={duration} onChange={(e) => setDuration(Number(e.target.value || 0))} className="mt-2 rounded-none border-ink-100 focus-visible:ring-safety" />
              </div>

              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Worker (optional)</Label>
                <Select value={workerId || "__none__"} onValueChange={(v) => setWorkerId(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="upload-worker" className="mt-2 rounded-none border-ink-100"><SelectValue placeholder="No worker" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No worker</SelectItem>
                    {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="mt-2 text-[10px] text-ink-500">
                  <Link to="/dashboard/workers" className="hover:text-safety underline underline-offset-2">Manage roster →</Link>
                </div>
              </div>

              <div>
                <Label htmlFor="notes" className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Notes (optional)</Label>
                <Textarea id="notes" data-testid="upload-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Anything we should know about the worker, station, or load?" className="mt-2 rounded-none border-ink-100 focus-visible:ring-safety" />
              </div>

              <label className="flex items-start gap-3 border border-ink-100 p-3 cursor-pointer hover:bg-ink-50 transition-colors" data-testid="blur-faces-row">
                <input
                  type="checkbox"
                  checked={blurFaces}
                  onChange={(e) => setBlurFaces(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-safety cursor-pointer"
                  data-testid="blur-faces-toggle"
                />
                <div className="flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-900">Blur faces (recommended)</div>
                  <div className="text-xs text-ink-700 mt-1 leading-relaxed">
                    All detected faces are anonymized with a Gaussian blur before storage or AI analysis. Uncheck only if you have explicit worker consent to store raw video.
                  </div>
                </div>
              </label>

              <div className="border border-ink-100 bg-ink-50 p-3 flex gap-2 items-start text-xs text-ink-700">
                <Warning size={14} weight="bold" className="text-safety mt-0.5 shrink-0" />
                <div>Videos are processed by an AI model. SafeForm.ai is a screening tool, not a clinical diagnosis.</div>
              </div>

              <Button
                data-testid="upload-submit"
                onClick={submit}
                disabled={!file || submitting}
                size="lg"
                className="w-full rounded-none bg-safety hover:bg-safety-hover text-white h-12 text-sm font-semibold disabled:opacity-50"
              >
                {submitting ? "Uploading…" : (<>Analyze now <ArrowRight size={16} weight="bold" className="ml-2" /></>)}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
